/**
 * Representation requests: an agent asking for a mandate, and the owner answering.
 */
import type {
  AnswerRepresentationRequest,
  RepresentationRequestsMadeResponse,
  RepresentationRequestsResponse,
  RequestRepresentationRequest,
  RequestRepresentationResponse,
} from "@ondo/shared";

import { ApiError, request } from "./request.ts";

/**
 * Ask a work's owner for a mandate, by ONDO code and one second fact.
 *
 * RETURNS ON A REFUSAL RATHER THAN THROWING — for the one caller in this file
 * that does. `request` turns a 403 into an `ApiError`, and this route answers
 * 403 for every refusal, so the refusal is caught here and handed back as the
 * verdict it is. That keeps the screen's model free of a try/catch whose two
 * branches would both be ordinary outcomes: a mistyped code is not an error,
 * and the form has to say the same sentence either way.
 *
 * A 429 is NOT caught. Being throttled is a different thing from being refused
 * — the controller says so by giving it a different status — and a screen that
 * showed "no such code" to somebody who had simply asked too often would be
 * telling them the opposite of the truth.
 */
export const requestRepresentation = async (
  body: RequestRepresentationRequest,
): Promise<RequestRepresentationResponse> => {
  try {
    return await request<RequestRepresentationResponse>("/representation-requests", body);
  } catch (cause: unknown) {
    if (cause instanceof ApiError && cause.status === 403) {
      return { verdict: "refused", requestId: null, workTitle: null, attributedName: null };
    }
    throw cause;
  }
};

/** What this account has asked for. The agent's own side. */
export const fetchRepresentationRequestsMade = () =>
  request<RepresentationRequestsMadeResponse>("/representation-requests/mine").then(
    (body) => body.requests,
  );

/**
 * What has been asked of this workspace. The owner's side.
 *
 * Null scopes to nothing and returns every request against every work this
 * account owns, which is what a personal workspace wants — the same convention
 * `fetchVerificationRequests` and `fetchAffiliationClaims` follow.
 */
export const fetchRepresentationRequests = (principalOndoId: string | null) =>
  request<RepresentationRequestsResponse>(
    principalOndoId
      ? `/representation-requests?principal=${encodeURIComponent(principalOndoId)}`
      : "/representation-requests",
  ).then((body) => body.requests);

export const answerRepresentationRequest = (
  id: string,
  body: AnswerRepresentationRequest,
) => request<void>(`/representation-requests/${id}/answer`, body);

export const withdrawRepresentationRequest = (id: string) =>
  request<void>(`/representation-requests/${id}/withdraw`, {});
